import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, Video, Camera, Layers, Sparkles, ArrowRight, Compass, PenTool, CheckCircle, Lightbulb, Sliders, Rocket } from 'lucide-react';
import { SERVICES } from '../data';
import ServiceModal from '../components/ServiceModal';
import PageHero from '../components/PageHero';

// ─── Animation variants ───
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
});

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const openModal = (service) => {
    setSelectedService(service);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedService(null);
  };

  const goToPortfolio = (filter) => {
    navigate(`/portfolio?filter=${filter}`);
  };

  const iconMap = {
    Palette: Palette,
    Video: Video,
    Camera: Camera,
    Layers: Layers,
  };

  const services = SERVICES.map((s) => ({
    ...s,
    icon: iconMap[s.icon],
  }));

  const processSteps = [
    { icon: Compass, title: 'Discover', description: 'Understanding the brief, audience, objectives, and creative challenge.' },
    { icon: Lightbulb, title: 'Concept', description: 'Developing the visual direction, ideas, references, and storytelling approach.' },
    { icon: PenTool, title: 'Create', description: 'Designing, filming, photographing, animating, and editing the work.' },
    { icon: Sliders, title: 'Refine', description: 'Reviewing, polishing, and ensuring every detail aligns with the intended message.' },
    { icon: Rocket, title: 'Deliver', description: 'Producing final creative assets that are ready to communicate, engage, and perform.' },
  ];

  return (
    <section className="min-h-screen py-24 bg-gradient-to-b from-black via-zinc-900 to-black pt-32 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-600/20 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse delay-1000" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* ─── Page Hero ─── */}
        <PageHero
          badge="Services"
          title="What I <span class='text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500'>Do</span>"
          subtitle="From concept to completion — I offer creative services including graphic design, videography, video editing, motion design, photography, and photo editing."
        />

        {/* ─── Service Cards – 2 columns ─── */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-24"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            if (!Icon) return null;
            return (
              <motion.div
                key={index}
                variants={fadeUp(0.1 * index)}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-yellow-500/40 transition-all duration-500 hover:shadow-2xl hover:shadow-yellow-500/10"
              >
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-500/20 to-yellow-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 group-hover:shadow-lg group-hover:shadow-yellow-500/20">
                    <Icon size={32} className="text-yellow-400" />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-gray-400 leading-relaxed mb-4">{service.longDescription}</p>
                  {/* <div className="flex flex-wrap gap-2">
                    {service.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10 text-gray-300"
                      >
                        {feature}
                      </span>
                    ))}
                  </div> */}

                  <div className="mt-6 flex items-center justify-between">
                    <button
                      onClick={() => openModal(service)}
                      className="text-sm text-yellow-400 hover:text-yellow-300 transition-colors flex items-center gap-1 group"
                    >
                      Learn More <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                      onClick={() => goToPortfolio(service.filterValue)}
                      className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1 group"
                    >
                      View Portfolio <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* ─── Process Section ─── */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 text-sm font-medium tracking-wider uppercase">
            {/* <Sparkles size={14} />  */}
            How I Work
          </span>
          <h3 className="font-serif text-3xl sm:text-4xl font-bold text-white mt-4">
            A <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500">Simple Process</span> for Great Results
          </h3>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid md:grid-cols-3 gap-8 mb-24"
        >
          {processSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                variants={fadeUp(0.1 * index)}
                whileHover={{ y: -4 }}
                className="relative group text-center p-8 rounded-2xl bg-white/5 border border-white/5 hover:border-yellow-500/30 transition-all duration-300"
              >
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-500/20 to-yellow-500/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Icon size={28} className="text-yellow-400" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-white mb-2">0{index + 1}</div>
                <h4 className="text-xl font-semibold text-white mb-2">{step.title}</h4>
                <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-yellow-400/30">
                    <ArrowRight size={24} />
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>

        {/* ─── Call to Action ─── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-12 border border-white/10">
            <h3 className="font-serif text-3xl font-bold text-white mb-4">
              Have an <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500">idea worth</span> bringing to life?
            </h3>
            <p className="text-gray-400 mb-8">
              Whether you need a visual campaign, brand content, photography, video, motion design, or a creative partner to help shape an idea, I’m always open to exploring new creative challenges.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-medium hover:shadow-2xl hover:shadow-yellow-500/30 transition-all duration-300 group"
            >
              Start a Conversation
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </motion.div>
      </div>

      {/* ─── Service Modal ─── */}
      <AnimatePresence>
        {modalOpen && selectedService && (
          <ServiceModal service={selectedService} onClose={closeModal} />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Services;